import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Skeleton, Flex } from 'antd';
import { StyledMachineOperatingHoursDashboardChart } from './MachineOperatingHoursDashboardChart.styles';
import { MachineOperatingHoursDashboardChartElement } from './MachineOperatingHoursDashboardChartElement/MachineOperatingHoursDashboardChartElement';
import { TotalMachineOperatingHoursDatum } from './interfaces/MachineOperatingHoursDashboardChart.types';
import { MachineOperatingHoursDashboardChartGlobalStyles } from './MachineOperatingHoursDashboardChart.global.styles';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { NoResultsEmpty } from 'lib/components/NoResultsEmpty/NoResultsEmpty';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';

const VISIBLE_ITEMS = 14;
const SCROLL_STEP = 12;

export interface MachineOperatingHoursDashboardChartProps {
  data: TotalMachineOperatingHoursDatum[];
  isLoading: boolean;
}

export const MachineOperatingHoursDashboardChart = ({
  data,
  isLoading,
}: MachineOperatingHoursDashboardChartProps): JSX.Element => {
  const { t } = useTranslation();
  const [startIndex, setStartIndex] = useState(0);

  const operatingHoursMaximum = useMemo(
    () =>
      Math.max(
        ...data.map(
          (datum: TotalMachineOperatingHoursDatum): number =>
            datum.operatingTimeForPeriod?.actualTotalOperatingTimeMs ?? 0
        )
      ),
    [data]
  );

  const sortedData = useMemo(() => {
    const sorterAscending = (a: TotalMachineOperatingHoursDatum, b: TotalMachineOperatingHoursDatum): number =>
      (a.operatingTimeForPeriod?.actualTotalOperatingTimeMs ?? 0) -
      (b.operatingTimeForPeriod?.actualTotalOperatingTimeMs ?? 0);
    const sorterDescending = (a: TotalMachineOperatingHoursDatum, b: TotalMachineOperatingHoursDatum): number =>
      sorterAscending(a, b) * -1;

    // We need to create a shallow copy of the data, so React updates the DOM after sorting. If we don't do this,
    // React doesn't know the contents of the array changed since it only checks for reference equality.
    return [...data].sort(sorterDescending);
  }, [data]);

  const maxStartIndex = Math.max(0, sortedData.length - VISIBLE_ITEMS);
  const visibleData = useMemo(() => sortedData.slice(startIndex, startIndex + VISIBLE_ITEMS), [sortedData, startIndex]);

  const isAtStart = startIndex === 0;
  const isAtEnd = startIndex >= maxStartIndex;

  useEffect(() => {
    setStartIndex(0);
  }, [sortedData]);

  useEffect(() => {
    setStartIndex(currentStartIndex => Math.min(currentStartIndex, maxStartIndex));
  }, [maxStartIndex]);

  const handlePrevious = (): void => setStartIndex(index => Math.max(0, index - SCROLL_STEP));
  const handleNext = (): void => setStartIndex(index => Math.min(index + SCROLL_STEP, maxStartIndex));

  const renderChartContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <Flex gap={kccThemeConstants.SPACING.SM} style={{ height: 'auto', overflow: 'auto' }}>
          <Skeleton active title={false} paragraph={{ rows: 10 }} loading={isLoading} />
          <Skeleton active title={false} paragraph={{ rows: 10 }} loading={isLoading} />
          <Skeleton active title={false} paragraph={{ rows: 10 }} loading={isLoading} />
        </Flex>
      );
    }
    if (data.length === 0) {
      return <NoResultsEmpty />;
    }
    return (
      <div className="machine-operating-hours-dashboard-chart__elements">
        {visibleData.map((datum, index) => (
          <MachineOperatingHoursDashboardChartElement
            className="machine-operating-hours-dashboard-chart__element"
            // While it is usually bad practice to use an index as a collection key in React and should be avoided
            // we doing it here on purpose so DOM elements are reused and the transition animation is applied
            // when changing pages or sorting
            key={`machine-operating-hours-dashboard-chart__element-index-${index}`}
            chartBarId={`machine-operating-hours-dashboard-chart__element-index-${index}`}
            totalMachineOperatingHoursDatum={datum}
            operatingHoursMaximum={operatingHoursMaximum}
          />
        ))}
      </div>
    );
  };

  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <MachineOperatingHoursDashboardChartGlobalStyles />
      <div
        className={classnames('machine-operating-hours-dashboard-chart__pagination', {
          'machine-operating-hours-dashboard-chart__pagination--disabled': isAtStart,
        })}
      >
        <div className="machine-operating-hours-dashboard-chart__pagination-container">
          <KccButton
            type="tertiary"
            icon={<SvgIcon name="chevronUp" />}
            disabled={isAtStart}
            onClick={handlePrevious}
            className={classnames('machine-operating-hours-dashboard-chart__pagination-button--previous', {
              'machine-operating-hours-dashboard-chart__pagination-button--disabled': isAtStart,
            })}
          />
        </div>
      </div>
      <StyledMachineOperatingHoursDashboardChart className="machine-operating-hours-dashboard-chart">
        <div className="machine-operating-hours-dashboard-chart__title-bar">
          <h4 className="machine-operating-hours-dashboard-chart__title">
            {t('overview.fleetDashboard.machineOperatingHoursDashboardChart.title')}
          </h4>
          <Tooltip title={t('overview.fleetDashboard.machineOperatingHoursDashboardChart.description')}>
            <div>
              <SvgIcon name="information" className="machine-operating-hours-dashboard-chart__info-icon" />
            </div>
          </Tooltip>
        </div>
        {renderChartContent()}
      </StyledMachineOperatingHoursDashboardChart>
      <div
        className={classnames('machine-operating-hours-dashboard-chart__pagination', {
          'machine-operating-hours-dashboard-chart__pagination--disabled': isAtEnd,
        })}
      >
        <div className="machine-operating-hours-dashboard-chart__pagination-container">
          <KccButton
            type="tertiary"
            icon={<SvgIcon name="chevronDown" />}
            disabled={isAtEnd}
            onClick={handleNext}
            className="machine-operating-hours-dashboard-chart__pagination-button--next"
          />
        </div>
      </div>
    </div>
  );
};
